import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { createSsoSetupLink, getSetupLinkUrl, getSsoStatus } from 'services/sso'
import { openEnterpriseEnquiryCalendar } from 'utils/calEmbed'

const readSsoFlags = (sso) => ({
  entitled: !!sso?.entitled,
  connected: !!(sso?.connected ?? sso?.exists)
})

/**
 * Enterprise SSO entitlement + IdP connection for the owner dashboard.
 * Prefer userProfile.sso from /v3/account/profile. Only call GET /v3/sso/status
 * when explicitly refreshing after setup (checkStatus).
 */
const useEnterpriseSso = ({ enabled = true } = {}) => {
  const profileSso = useSelector((state) => state.manage.userProfile?.sso)
  const [override, setOverride] = useState(null)
  const [checking, setChecking] = useState(false)
  const [openingSetup, setOpeningSetup] = useState(false)

  const { entitled, connected } = enabled ? readSsoFlags(override ?? profileSso) : { entitled: false, connected: false }

  const checkStatus = useCallback(async () => {
    if (!enabled) {
      setOverride({ entitled: false, connected: false })
      return { entitled: false, connected: false }
    }

    setChecking(true)
    try {
      const { data } = await getSsoStatus()
      const next = { entitled: true, connected: !!data?.exists }
      setOverride(next)
      return next
    } catch (err) {
      const next = { entitled: false, connected: false }
      setOverride(next)
      if (err?.response?.status === 403) {
        return next
      }
      return { ...next, error: err }
    } finally {
      setChecking(false)
    }
  }, [enabled])

  const openSetup = useCallback(async ({ regenerate = false } = {}) => {
    setOpeningSetup(true)
    try {
      const { data } = await createSsoSetupLink(regenerate ? { regenerate: true } : {})
      const url = getSetupLinkUrl(data?.setupLink)
      if (!url) {
        throw new Error('Setup link URL missing from response')
      }
      window.open(url, '_blank', 'noopener,noreferrer')
      return url
    } finally {
      setOpeningSetup(false)
    }
  }, [])

  /**
   * Resolve a site access-mode click for SSO.
   * @returns {boolean} true if the mode can be applied on the site
   */
  const selectSsoMode = useCallback(() => {
    if (!enabled) return false

    if (!entitled) {
      openEnterpriseEnquiryCalendar()
      return false
    }

    if (!connected) {
      openSetup().catch((err) => {
        window.alert(err?.message || 'Failed to open SSO setup. Please try again.')
      })
      return false
    }

    return true
  }, [enabled, entitled, connected, openSetup])

  return {
    loading: false,
    checking,
    openingSetup,
    entitled,
    connected,
    needsSetup: entitled && !connected,
    checkStatus,
    openSetup,
    selectSsoMode
  }
}

export default useEnterpriseSso
